// File: src/features/variant_workflow/molecular_impact/SilicoPredictionToolEditModal.tsx
import { Alert, Button, Input, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { SilicoPredictionType } from '../redux';
import { silicoPredictionToolEditValidationSchema } from '../validation_schemas/silico_tool_edit_validation_schema';
import { useSilicoPredictionToolEditModalLogic } from './hooks';

interface SilicoPredictionToolEditModalProps {
  variant_id: string;
  open: boolean;
  onCancel: () => void;
  tool: SilicoPredictionType & { id?: number };
}

const SilicoPredictionToolEditModal: React.FC<
  SilicoPredictionToolEditModalProps
> = ({ variant_id, open, onCancel, tool }) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isModalLoading,
    commentEditorState,
    setCommentEditorState,
    predictionResultChoices,
    shouldUsePredictionResultSelect,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleSubmit,
    selectLabelFilterOption,
  } = useSilicoPredictionToolEditModalLogic({
    variant_id,
    open,
    onCancel,
    tool,
  });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isModalLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={silicoPredictionToolEditValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          handleBlur,
          isValid,
          dirty,
        }) => {
          // Centralized disable logic via getIsSaveDisabled
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Prediction tool:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    value={tool.tool}
                    disabled
                    className="h-proband-modal__input-field-disabled-highlight"
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Score:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="score"
                    value={values.score}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Numeric value, e.g. 0.87"
                    readOnly={!canInteract}
                    status={errors.score ? 'error' : undefined}
                  />
                  <div
                    className={`c-form-field__hint${
                      errors.score ? ' c-form-field__hint--error' : ''
                    }`}
                  >
                    {errors.score ||
                      'Expected format: integer or decimal (e.g. 0.943, 101)'}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Prediction result:</div>
                <div className="c-form-field__wrapper">
                  {shouldUsePredictionResultSelect ? (
                    <Select
                      value={values.prediction_result || undefined}
                      onChange={(v) => {
                        void setFieldValue('prediction_result', v);
                      }}
                      onBlur={() => handleBlur('prediction_result')}
                      className="h-proband-modal__select-field"
                      options={predictionResultChoices}
                      placeholder="Select prediction result"
                      showSearch
                      filterOption={selectLabelFilterOption}
                      disabled={!canInteract}
                      status={!canInteract ? 'error' : undefined}
                    />
                  ) : (
                    <Input
                      name="prediction_result"
                      value={values.prediction_result}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. Damaging"
                      readOnly={!canInteract}
                    />
                  )}
                  <div className="c-form-field__error">
                    {errors.prediction_result && touched.prediction_result
                      ? errors.prediction_result
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Comment:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment"
                    editorState={commentEditorState}
                    onChange={(newState) => {
                      setCommentEditorState(newState);
                      const htmlContent = editorStateToHtml(newState);
                      void setFieldValue('comment', htmlContent);
                    }}
                    onBlur={() => handleBlur('comment')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.comment ? (errors.comment as any) : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SilicoPredictionToolEditModal;
